import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { isListingExpired } from '#/modules/listing/helpers/isListingExpired';
import { listingApiIsomorphicFetchJob } from '#/modules/listing/jobs/api/listingApiFetchJob';
import { ExpiredJobPageLoadable } from '#/modules/listing/jobs/pages/expired/ExpiredJobPageLoadable';
import { UnpublishedJobPageLoadable } from '#/modules/listing/jobs/pages/unpublished/UnpublishedJobPageLoadable';
import { viewJobReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { userDashboardApiIsomorphicFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';

import { ViewJobPageLoadable } from './ViewJobPageLoadable';

export const handle = {
  defaultSearchType: 'JOB',
  showDesktopSearch: true,
  showSearchRefinements: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { jobIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewJobReferenceRoute>) {
  const [jobId] = jobIdDashSlug.split('-');

  try {
    const [job] = await Promise.all([
      listingApiIsomorphicFetchJob({ request, context })(jobId),
      // It's possible this loadable won't be used if the job is unpublished
      // but in most cases optimistically loading it is best
      ViewJobPageLoadable.load(),
    ]);

    const fetchedListingExtras =
      await userDashboardApiIsomorphicFetchListingExtras({
        request,
        context,
      })([jobId, job.org?.id].filter(Boolean));

    const { saved, applicationId } = fetchedListingExtras[jobId] ?? {
      saved: false,
      applicationId: null,
    };

    const orgSaved = Boolean(
      job.org && fetchedListingExtras[job.org.id]?.saved,
    );

    switch (job.type) {
      case 'JOB':
        return {
          type: isListingExpired(job.expires) ? 'EXPIRED_JOB' : job.type,
          job,
          saved,
          applicationId,
          orgSaved,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: job.url,
          } satisfies LayoutHandleProps,
        } as const;
      case 'UNPUBLISHED_JOB':
        await UnpublishedJobPageLoadable.load();
        return {
          type: 'UNPUBLISHED_JOB',
          unpublishedJob: job,
        } as const;
    }
  } catch {
    return {
      type: 'NOT_FOUND',
    } as const;
  }
}

export default function ViewJobHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  // NOTE: UNPUBLISHED_JOB is not the same as Job.isPublished === false
  // UNPUBLISHED_JOB is returned when we are serving an unpublished (hidden) job
  // to a user who has not applied to it.
  // Previous Applicants would fall under the JOB branch (Job.type === 'JOB')
  // Context: https://idealist.slack.com/archives/CHJ9JK7TP/p1749664131909249?thread_ts=1747839567.113479&cid=CHJ9JK7TP
  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <NotFoundPageLoadable noLayout />;
    case 'JOB':
      return (
        <RouteModule
          title={`${loaderData.job.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['JOB']}`}
        >
          <ViewJobPageLoadable
            job={loaderData.job}
            initialListingApplicationId={loaderData.applicationId}
            initiallySaved={loaderData.saved}
            orgInitiallySaved={loaderData.orgSaved}
          />
        </RouteModule>
      );
    case 'EXPIRED_JOB':
      return (
        <RouteModule title={loaderData.job.name}>
          <ExpiredJobPageLoadable
            job={loaderData.job}
            listingApplicationId={loaderData.applicationId}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_JOB':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <UnpublishedJobPageLoadable
            unpublishedJob={loaderData.unpublishedJob}
          />
        </RouteModule>
      );
  }
}
